import { ProductDetailsModalContextProvider } from '@app/components/products/ProductDetails/context/ProductDetailsModalContext';
import { ProductDetailsModal } from '@app/components/products/ProductDetails/ProductDetailsModal';
import { clearFailedProducts, getFailedProducts, getIsDeliveryWindowsLoading, getOrderDetails } from '@app/ducks';
import { useDownloadOrderExcel } from '@app/services/hooks/documents/useDownloadOrderExcel';
import { useAmplitude, useUrlProductId } from '@app/utils/hooks';
import { useModalToggle } from '@app/utils/hooks/useModalToggle';
import { useUrlVariantId } from '@app/utils/hooks/useUrlVariantId';
import { isNull } from '@app/utils/is';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { DownloadLinesheetModal } from '../DownloadLinesheetModal';
import { OrderBottomBar } from '../OrderBottomBar';
import { OrderInformation } from '../OrderInformation';
import { OrderProducts } from '../OrderProducts';
import { OrderGeneralAlerts } from '../OrderProducts/OrderGeneralAlerts';
import { OrderProductsAlerts } from '../OrderProducts/OrderProductsAlerts';
import { useOrderProductsContext } from '../OrderProducts/OrderProductsContext';

import { OrderDetailsHeader } from './OrderDetailsHeader';

export const OrderDetailsPageContent = () => {
  const { id } = useParams<{ id: string }>();
  const productId = useUrlProductId();
  const variantId = useUrlVariantId();
  const { sortedProducts } = useOrderProductsContext();
  const dispatch = useDispatch();
  const orderDetails = useSelector(getOrderDetails);
  const isLoadingDeliveryWindows = useSelector(getIsDeliveryWindowsLoading);
  const { trackEvent } = useAmplitude();
  const { t } = useTranslation(['accounts', 'common', 'orders', 'selections', 'products', 'linesheets', 'checkout']);
  const { showModal: showLinesheetModal, isOpen: isLinesheetModalOpen, hideModal: hideLinesheetModal } = useModalToggle();
  const failedProductsData = useSelector(getFailedProducts);
  const title = t('selections:selection_number', {
    orderNumber: orderDetails.order.orderNumber,
  });
  const { mutate: downloadOrderExcel } = useDownloadOrderExcel();
  const handleDownloadOrderExcel = React.useCallback(() => {
    const { order } = orderDetails;
    downloadOrderExcel({ orderId: order.order, orderNumber: order.orderNumber });
    trackEvent({ name: 'downloadAction.button.orderDetailsXls.submit', properties: { path: 'mySelection' } });
  }, [downloadOrderExcel, orderDetails, trackEvent]);

  React.useEffect(() => {
    return () => {
      if (!isNull(failedProductsData)) {
        dispatch(clearFailedProducts());
      }
    };
  }, [failedProductsData, dispatch]);

  const handleSuccessDownloadLinesheetModal = () => {
    trackEvent({ name: 'downloadAction.button.linesheet.submit', properties: { path: 'mySelection' } });
  };

  return (
    <>
      <OrderBottomBar location="selection" orderId={id} />

      <div className="mx-auto w-full max-w-screen-xl px-4 lg:px-8">
        <OrderGeneralAlerts />
        <OrderDetailsHeader
          onDownloadLineseet={showLinesheetModal}
          onDownloadOrderExcel={handleDownloadOrderExcel}
          title={title}
          name={orderDetails.order.name}
          isLoading={isLoadingDeliveryWindows}
        />
        <div className="border-b border-neutral-90 pb-8">
          <OrderInformation orderDetails={orderDetails} isLoading={isLoadingDeliveryWindows} isEditable />
        </div>
        <OrderProductsAlerts
          orderDetails={orderDetails}
          allProducts={orderDetails.products}
          deletedProducts={orderDetails.order.deletedProducts}
        />
        <div className="pb-8">
          <OrderProducts isLoading={isLoadingDeliveryWindows} />
        </div>
      </div>
      <DownloadLinesheetModal
        isOpen={isLinesheetModalOpen}
        onClose={hideLinesheetModal}
        onSuccess={handleSuccessDownloadLinesheetModal}
        orderId={orderDetails.order.order}
        orderNumber={orderDetails.order.orderNumber}
        location="select units"
        downloadOnlySelected={false}
      />
      <ProductDetailsModalContextProvider productId={productId} products={sortedProducts} variantId={variantId}>
        <ProductDetailsModal />
      </ProductDetailsModalContextProvider>
    </>
  );
};
